import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Link
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';

import { getAllScheduleStatuses, updateScheduleStatus } from '../services/scheduler.service';
import { IScheduleStatusSummary } from '../types/interfaces/scheduleStatus.interface';

const getIsEnabled = (schedule: IScheduleStatusSummary) => {
  if (typeof schedule.enabled === 'boolean') {
    return schedule.enabled;
  }

  if (typeof schedule.status === 'string') {
    const normalized = schedule.status.toLowerCase();
    return normalized === 'enabled' || normalized === 'active' || normalized === 'true' || normalized === '1';
  }

  return false;
};

const getIsPaused = (schedule: IScheduleStatusSummary) => {
  if (typeof schedule.paused === 'boolean') {
    return schedule.paused;
  }

  if (typeof schedule.status === 'string') {
    const normalized = schedule.status.toLowerCase();
    return normalized === 'paused' || normalized === 'pause';
  }

  return false;
};

const getStatusLabel = (schedule: IScheduleStatusSummary) => {
  if (getIsPaused(schedule)) {
    return 'Paused';
  }

  return getIsEnabled(schedule) ? 'Enabled' : 'Disabled';
};

const normalizeSchedule = (item: any): IScheduleStatusSummary => ({
  projectId: item?.projectId ?? item?.testProjectId ?? item?.project?.id ?? item?.project?.projectId ?? 0,
  scheduleId: item?.scheduleId ?? item?.testScheduleId ?? item?.id ?? 0,
  scheduleName: item?.scheduleName ?? item?.name,
  status: item?.status,
  enabled: typeof item?.enabled === 'boolean' ? item.enabled : undefined,
  paused: typeof item?.paused === 'boolean' ? item.paused : undefined
});

const formatDateTime = (value?: string) => {
  if (!value) {
    return '—';
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : value;
};

const AllSchedules: React.FC = () => {
  const [schedules, setSchedules] = useState<IScheduleStatusSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ id: number; type: 'toggle' | 'pause' | 'resume' } | null>(null);
  const navigate = useNavigate(); // Move useNavigate to the top level of the component

  const handleNavigation = (url: string) => {
    navigate(url); // Navigate to the specified URL
  };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllScheduleStatuses();
      const listCandidate = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.content)
          ? response.content
          : Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response)
              ? response
              : [];

      const normalized = listCandidate.map(normalizeSchedule)
        .filter((schedule: any) => (
          schedule.projectId !== undefined && schedule.projectId !== null &&
          schedule.scheduleId !== undefined && schedule.scheduleId !== null
        ));

      setSchedules(normalized);
    } catch (err) {
      console.error('Failed to load schedules', err);
      setSchedules([]);
      setError('Failed to load schedules. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleToggle = useCallback(async (schedule: IScheduleStatusSummary) => {
    if (
      schedule.projectId === undefined || schedule.projectId === null ||
      schedule.scheduleId === undefined || schedule.scheduleId === null
    ) {
      return;
    }

    const nextEnabled = !getIsEnabled(schedule);
    setPendingAction({ id: schedule.scheduleId, type: 'toggle' });
    setError(null);

    try {
      await updateScheduleStatus(schedule.projectId, schedule.scheduleId, nextEnabled);
      setSchedules((prev) => prev.map((item) => (
        item.scheduleId === schedule.scheduleId
          ? {
              ...item,
              enabled: nextEnabled,
              status: nextEnabled ? 'ENABLED' : 'DISABLED',
              paused: false,
            }
          : item
      )));
    } catch (err) {
      console.error('Failed to update schedule status', err);
      setError('Failed to update the schedule status. Please try again.');
    } finally {
      setPendingAction(null);
    }
  }, []);

  const handlePause = useCallback(async (schedule: IScheduleStatusSummary) => {
    if (
      schedule.projectId === undefined || schedule.projectId === null ||
      schedule.scheduleId === undefined || schedule.scheduleId === null
    ) {
      return;
    }

    setPendingAction({ id: schedule.scheduleId, type: 'pause' });
    setError(null);

    try {
      await updateScheduleStatus(schedule.projectId, schedule.scheduleId, 'PAUSED');
      setSchedules((prev) => prev.map((item) => (
        item.scheduleId === schedule.scheduleId
          ? {
              ...item,
              status: 'PAUSED',
              paused: true,
              enabled: true,
            }
          : item
      )));
    } catch (err) {
      console.error('Failed to pause schedule', err);
      setError('Failed to pause the schedule. Please try again.');
    } finally {
      setPendingAction(null);
    }
  }, []);

  const handleResume = useCallback(async (schedule: IScheduleStatusSummary) => {
    if (
      schedule.projectId === undefined || schedule.projectId === null ||
      schedule.scheduleId === undefined || schedule.scheduleId === null
    ) {
      return;
    }

    setPendingAction({ id: schedule.scheduleId, type: 'resume' });
    setError(null);

    try {
      await updateScheduleStatus(schedule.projectId, schedule.scheduleId, 'ENABLED');
      setSchedules((prev) => prev.map((item) => (
        item.scheduleId === schedule.scheduleId
          ? {
              ...item,
              status: 'ENABLED',
              paused: false,
              enabled: true,
            }
          : item
      )));
    } catch (err) {
      console.error('Failed to resume schedule', err);
      setError('Failed to resume the schedule. Please try again.');
    } finally {
      setPendingAction(null);
    }
  }, []);

  const content = useMemo(() => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (!schedules.length) {
      return (
        <Box py={6} textAlign="center">
          <Typography variant="body1">No schedules found.</Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="10%">Project ID</TableCell>
              <TableCell width="10%">Schedule ID</TableCell>
              <TableCell width="25%">Schedule Name</TableCell>
              <TableCell width="15%" align="center">Status &amp; Controls</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((schedule) => {
              const isEnabled = getIsEnabled(schedule);
              const isPaused = getIsPaused(schedule);
              const statusLabel = getStatusLabel(schedule);
              const statusColor = isPaused
                ? 'warning.main'
                : isEnabled
                  ? 'success.main'
                  : 'text.secondary';
              const hasIdentifiers =
                schedule.projectId !== undefined && schedule.projectId !== null &&
                schedule.scheduleId !== undefined && schedule.scheduleId !== null;
              const handleScheduleClick = () => {
                if (hasIdentifiers) {
                  handleNavigation(`/projects/${schedule.projectId}/scheduled/testresults/${schedule.scheduleId}`);
                }
              };
              const actionInFlight = pendingAction?.id === schedule.scheduleId;
              const isTogglePending = actionInFlight && pendingAction?.type === 'toggle';
              const isPausePending = actionInFlight && pendingAction?.type === 'pause';
              const isResumePending = actionInFlight && pendingAction?.type === 'resume';
              const disableToggle = !hasIdentifiers || actionInFlight;
              const canPause = hasIdentifiers && isEnabled && !isPaused;
              const canResume = hasIdentifiers && isEnabled && isPaused;
              const pauseResumeDisabled = actionInFlight || (isPaused ? !canResume : !canPause);
              const pauseResumeLabel = isPaused ? 'Resume' : 'Pause';
              const pauseResumeTooltip = !hasIdentifiers
                ? 'Schedule identifiers are unavailable.'
                : !isEnabled
                  ? 'Enable the schedule before using pause or resume.'
                  : isPaused
                    ? 'Resume this schedule.'
                    : 'Pause this schedule.';

              return (
                <TableRow key={`${schedule.projectId}-${schedule.scheduleId}`} hover>
                  <TableCell><Link onClick={() => handleNavigation(`/projects/${schedule.projectId}/scheduler`)} >{schedule.projectId}</Link></TableCell>
                  <TableCell>
                    {hasIdentifiers ? (
                      <Link
                        component="button"
                        onClick={handleScheduleClick}
                        sx={{ cursor: 'pointer' }}
                      >
                        {schedule.scheduleId}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{schedule.scheduleName || '—'}</TableCell>
                  <TableCell align="center">
                    <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
                      <Typography variant="body2" color={statusColor} fontWeight={600}>
                        {statusLabel}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                        <Switch
                          edge="end"
                          color="primary"
                          checked={isEnabled}
                          disabled={disableToggle}
                          onChange={() => handleToggle(schedule)}
                          inputProps={{
                            'aria-label': `Toggle schedule ${schedule.scheduleName || schedule.scheduleId}`,
                            'aria-busy': isTogglePending || undefined,
                          }}
                        />
                        {/* <Tooltip
                          title={pauseResumeTooltip}
                          disableHoverListener={!pauseResumeDisabled || actionInFlight}
                        >
                          <span>
                            <Button
                              variant="outlined"
                              size="small"
                              color={isPaused ? 'success' : 'warning'}
                              disabled={pauseResumeDisabled}
                              onClick={() => (isPaused ? handleResume(schedule) : handlePause(schedule))}
                              startIcon={
                                isPausePending || isResumePending ? (
                                  <CircularProgress color="inherit" size={16} />
                                ) : undefined
                              }
                            >
                              {pauseResumeLabel}
                            </Button>
                          </span>
                        </Tooltip> */}
                      </Stack>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }, [handlePause, handleResume, handleToggle, loading, pendingAction, schedules]);

  return (
    <Box p={3} display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">All Project Schedules</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchSchedules} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {content}
    </Box>
  );
};

export default AllSchedules;